import type { JSX } from 'react';
import { EmptyState } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { useClassDocuments } from '../lib/queries';
import { AppWindow } from '../window/AppWindow';
import { t } from '../messages';
import { DocumentList } from './DocumentList';
import './documents.css';

/**
 * **« Documents » — l'entrée promise à l'élève par le §5 du cahier des charges, et qui n'existait
 * pas.**
 *
 * Ce qu'il y avait avant : `core.resources` en base, avec sa politique de sécurité, et rien
 * au-dessus. Un enseignant ne pouvait déposer ni PDF ni consigne Word ; un élève n'avait aucun
 * endroit où retrouver ce qu'on lui avait remis. Le formulaire d'activité acceptait pourtant une
 * « ressource jointe » et la rangeait en base — un champ mort, exactement le genre de promesse
 * creuse que ce dépôt traque.
 *
 * **Une page du cœur, dans une fenêtre du cœur** — le même patron que « Mon compte » et
 * « Activité du jour », et rien de spécial accordé : ni fenêtre à part, ni barre de titre, ni plein
 * écran réinventés.
 *
 * **Le même écran pour l'élève et pour l'enseignant.** L'adulte a besoin de voir ce que sa classe
 * a sous les yeux ; ce qu'il peut faire de PLUS (déposer, retirer) vit dans son espace, section
 * « Documents ». Le bouton ci-dessous l'y emmène — jamais une phrase qui lui demande d'aller
 * chercher ailleurs.
 */
export function DocumentsPage(): JSX.Element {
  const { classId, me, openTeacher } = useDesk();
  const documents = useClassDocuments(classId);

  let body: JSX.Element;
  if (documents.isPending) {
    body = <p className="documents-notice">{t('documents.loading')}</p>;
  } else if (documents.isError || documents.data === undefined) {
    body = (
      <p className="ndrc-error">
        {documents.error instanceof Error ? documents.error.message : t('desk.errorTitle')}
      </p>
    );
  } else if (documents.data.documents.length === 0) {
    body = (
      <EmptyState title={t('documents.emptyTitle')} hint={t('documents.emptyHint')}>
        {me.role === 'teacher' ? (
          <button
            type="button"
            className="ndrc-button ndrc-button--primary"
            onClick={() => {
              openTeacher('documents');
            }}
          >
            {t('documents.emptyAction')}
          </button>
        ) : null}
      </EmptyState>
    );
  } else {
    body = <DocumentList documents={documents.data.documents} />;
  }

  return (
    <AppWindow title={t('documents.title')}>
      <div className="documents-page">
        <section className="documents">
          <header className="documents-head">
            <h2 className="documents-title">{t('documents.title')}</h2>
            <p className="documents-hint">{t('documents.hint')}</p>
          </header>
          {body}
        </section>
      </div>
    </AppWindow>
  );
}
